// commons.js
// Métodos utilitarios comunes para los componentes dentro de 'apps'

import { APP_PREFIX_PATH } from 'configs/AppConfig';

/**
 * Formatea una fecha a 'DD/MM/YYYY'
 * @param {Date|string} date
 * @returns {string}
 */
export function formatDate(date) {
    const d = new Date(date);
    if (isNaN(d)) return '';
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = d.getFullYear();
    return `${day}/${month}/${year}`;
}

/**
 * Capitaliza la primera letra de un string
 * @param {string} str
 * @returns {string}
 */
export function capitalize(str) {
    if (!str) return '';
    return str.charAt(0).toUpperCase() + str.slice(1);
}

/**
 * Valida si un valor es un email válido
 * @param {string} email
 * @returns {boolean}
 */
export function isValidEmail(email) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}

/**
 * Devuelve una copia profunda de un objeto
 * @param {object} obj
 * @returns {object}
 */
export function deepClone(obj) {
    return JSON.parse(JSON.stringify(obj));
}

// Claves de sessionStorage usadas por el flujo de reporte de errores.
const APP_CTX_KEY = 'openAppContext';
const BUG_CTX_KEY = 'bugReportContext';

function readJSON(storage, key) {
  try { return JSON.parse(storage.getItem(key)) || {}; }
  catch { return {}; }
}

/**
 * Registra qué módulo tiene montado AppPopup. Los módulos Unity se abren en un
 * popup SIN cambiar la ruta del navegador, así que window.location apunta a la
 * pantalla de fondo (normalmente /app/home) y no sirve para identificarlos.
 * @param {string} path  Ruta del módulo en appModules
 * @param {string} title Título mostrado en el popup
 */
export const setOpenAppContext = (path, title) => {
  try { sessionStorage.setItem(APP_CTX_KEY, JSON.stringify({ path, title })); }
  catch { /* sessionStorage no disponible */ }
};

export const clearOpenAppContext = () => {
  try { sessionStorage.removeItem(APP_CTX_KEY); }
  catch { /* sessionStorage no disponible */ }
};

/**
 * Arma el contexto de lo que el usuario está viendo, para adjuntarlo al reporte.
 * Combina el módulo abierto en el popup con la actividad del BusDeDatos (el
 * mismo que se envía a Unity, ver utils/unityBus.js).
 * @returns {{returnUrl:string, modulo:string, moduloTitulo:string, actividad:string, bloque:string, uidActividad:string, uidSecuencia:string}}
 */
export function buildBugContext() {
  const app = readJSON(sessionStorage, APP_CTX_KEY);
  const currentUrl = window.location.pathname + window.location.search;
  const enModulo = Boolean(app.path) || currentUrl.startsWith(`${APP_PREFIX_PATH}/apps/`);
  // BusDeDatos conserva la ÚLTIMA actividad lanzada y no se limpia al cerrarla,
  // así que solo se adjunta cuando el reporte sale de un módulo en curso.
  const bus = enModulo ? readJSON(window.localStorage, 'BusDeDatos') : {};
  return {
    returnUrl:    currentUrl,
    modulo:       window.location.origin + (app.path || currentUrl),
    moduloTitulo: app.title || bus.nombreActividad || '',
    actividad:    bus.nombreActividad || '',
    bloque:       bus.NombreBloque || '',
    uidActividad: bus.uidActividad || '',
    uidSecuencia: bus.uidSecuencia || '',
  };
}

/** Contexto en una línea, para incrustarlo en el cuerpo del correo. */
export function formatBugContext(ctx = {}) {
  return [
    ctx.moduloTitulo && `Módulo: ${ctx.moduloTitulo}`,
    ctx.bloque       && `Bloque: ${ctx.bloque}`,
    ctx.actividad    && `Actividad: ${ctx.actividad}`,
    ctx.uidActividad && `uidActividad: ${ctx.uidActividad}`,
    ctx.uidSecuencia && `uidSecuencia: ${ctx.uidSecuencia}`,
  ].filter(Boolean).join(' · ');
}

/** Lee el contexto que dejó goToBugURL antes de navegar a /app/bug-report. */
export function readBugContext() {
  return readJSON(sessionStorage, BUG_CTX_KEY);
}

export const clearBugContext = () => {
  try { sessionStorage.removeItem(BUG_CTX_KEY); }
  catch { /* sessionStorage no disponible */ }
};

export const goToBugURL = () => {
  const ctx = buildBugContext();
  try { sessionStorage.setItem(BUG_CTX_KEY, JSON.stringify(ctx)); }
  catch { /* sessionStorage no disponible */ }
  sessionStorage.setItem('bugReportReturnUrl', ctx.returnUrl);
  // La navegación recarga el SPA y destruye el popup, así que el módulo abierto
  // deja de estar vigente: se consume aquí para no arrastrarlo al volver.
  clearOpenAppContext();
  // Marca esta navegación como interna intencional para que el auto-logout de
  // sesiones LTI (ver src/App.js) NO cierre la sesión al recargar la página.
  sessionStorage.setItem('lti_skip_logout', '1');
  window.location.href = '/app/bug-report';
};